// The domain tree behind the document picker's two-row tab bar.
//
// `GET /domains` is the only place the parent of a domain is known: a document
// carries its own domain's id and name, never its parent's. So a tab bar that
// groups Train-1 and Train-2 under Ammonia has to index that listing first and
// resolve each document through it.
//
// Until the listing arrives the index is empty. Every function here answers for
// an empty index the way it answered before there was a tree at all — one flat
// row of domains, labelled by the name the document already carries — so the
// picker renders correctly on the first paint and gains its second row when the
// listing lands.
import type { AccountDomains, PidDocument } from "../types";
import { UNASSIGNED_DOMAIN, domainLabel, getDocumentDomainKey } from "./documentLabels";

/** One domain, flattened out of the account grouping `GET /domains` returns. */
export interface DomainNode {
  id: string;
  name: string;
  parentId: string | null;
  accountId: string;
  accountName: string | null;
}

export type DomainIndex = ReadonlyMap<string, DomainNode>;

export const EMPTY_DOMAIN_INDEX: DomainIndex = new Map<string, DomainNode>();

/**
 * Which documents a tab shows.
 *
 * `subtree` is a parent and its children together; `domain` is one domain's own
 * documents. They differ only for a parent that holds documents itself, which
 * is why the second row can show both "All Ammonia" and "Direct".
 */
export type DomainScope =
  | { kind: "all" }
  | { kind: "unassigned" }
  | { kind: "subtree"; domainId: string }
  | { kind: "domain"; domainId: string };

export const ALL_DOMAINS_SCOPE: DomainScope = { kind: "all" };

export interface DomainTab {
  /** Stable identity for React and for the selected-tab comparison. */
  key: string;
  label: string;
  count: number;
  scope: DomainScope;
}

export function buildDomainIndex(accounts: AccountDomains[]): DomainIndex {
  const index = new Map<string, DomainNode>();
  for (const account of accounts) {
    for (const domain of account.domains) {
      index.set(domain.id, {
        id: domain.id,
        name: domain.name,
        parentId: domain.parent_id,
        accountId: account.account_id,
        accountName: account.account_name,
      });
    }
  }
  return index;
}

/**
 * The top-row tab a document sits under: its domain's parent when it has one,
 * otherwise the domain itself.
 */
export function getDocumentTopDomainKey(index: DomainIndex, doc: PidDocument): string {
  const domainId = doc.domain_id;
  if (!domainId) return UNASSIGNED_DOMAIN;
  const node = index.get(domainId);
  // A domain the listing does not carry is its own top-level tab. Dropping it
  // into Unassigned would hide a document that does have a domain.
  if (!node?.parentId) return domainId;
  return node.parentId;
}

/**
 * `Ammonia > Train-1` for a child domain and `PHD` for a root, or
 * `fallbackLabel` when the index has not loaded or does not carry the domain.
 *
 * The fallback is the caller's because only the caller knows what else names
 * that domain: a document carries its own domain's name, while a picker holding
 * some other account's domain has nothing but the identifier.
 */
export function domainPathLabel(
  index: DomainIndex,
  domainId: string,
  fallbackLabel: string,
): string {
  const node = index.get(domainId);
  if (!node) return fallbackLabel;
  const parent = node.parentId ? index.get(node.parentId) : undefined;
  return parent ? `${parent.name} > ${node.name}` : node.name;
}

/**
 * The path label for the domain a document sits in, `Unassigned` when it sits in
 * none, and the name the document itself carries until the listing lands.
 */
export function documentDomainPathLabel(index: DomainIndex, doc: PidDocument): string {
  const domainId = doc.domain_id;
  if (!domainId) return "Unassigned";
  return domainPathLabel(index, domainId, domainLabel(doc.plant));
}

/** The name of one domain, for a tab or a dropdown. */
export function domainNameById(index: DomainIndex, domainId: string): string {
  return index.get(domainId)?.name ?? domainId;
}

export function documentInScope(
  index: DomainIndex,
  doc: PidDocument,
  scope: DomainScope,
): boolean {
  switch (scope.kind) {
    case "all":
      return true;
    case "unassigned":
      return !doc.domain_id;
    case "domain":
      return doc.domain_id === scope.domainId;
    case "subtree":
      return (
        doc.domain_id === scope.domainId ||
        getDocumentTopDomainKey(index, doc) === scope.domainId
      );
  }
}

function scopeKey(scope: DomainScope): string {
  switch (scope.kind) {
    case "all":
      return "all";
    case "unassigned":
      return UNASSIGNED_DOMAIN;
    case "subtree":
      return `subtree:${scope.domainId}`;
    case "domain":
      return `domain:${scope.domainId}`;
  }
}

export function sameScope(a: DomainScope, b: DomainScope): boolean {
  return scopeKey(a) === scopeKey(b);
}

function byLabel(a: DomainTab, b: DomainTab): number {
  return a.label.localeCompare(b.label);
}

/**
 * Row one: every parent domain and childless root that holds documents, plus
 * All and Unassigned. A domain with no documents anywhere beneath it gets no
 * tab — an SME filtering a list has no use for a tab that empties it.
 */
export function buildTopDomainTabs(index: DomainIndex, documents: PidDocument[]): DomainTab[] {
  const counts = new Map<string, number>();
  const names = new Map<string, string>();
  for (const doc of documents) {
    const key = getDocumentTopDomainKey(index, doc);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    if (key === doc.domain_id) names.set(key, domainLabel(doc.plant));
  }

  const total = documents.length;
  const domainTabs: DomainTab[] = [];
  for (const [key, count] of counts) {
    if (key === UNASSIGNED_DOMAIN) continue;
    domainTabs.push({
      key,
      // A parent holding only children never appears on a document, so its name
      // comes from the index; a childless root can use either.
      label: index.get(key)?.name ?? names.get(key) ?? key,
      count,
      scope: { kind: "subtree", domainId: key },
    });
  }
  domainTabs.sort(byLabel);

  const tabs: DomainTab[] = [
    { key: "all", label: "All Projects", count: total, scope: ALL_DOMAINS_SCOPE },
    ...domainTabs,
  ];
  const unassigned = counts.get(UNASSIGNED_DOMAIN);
  if (unassigned) {
    tabs.push({
      key: UNASSIGNED_DOMAIN,
      label: "Unassigned",
      count: unassigned,
      scope: { kind: "unassigned" },
    });
  }
  return tabs;
}

/**
 * Row two, for the selected top-level tab: the whole subtree, then each child
 * that holds documents, then the parent's own documents as "Direct".
 *
 * Returns an empty list when the tab has no children to split — a childless
 * root needs no second row, and neither does All or Unassigned.
 */
export function buildChildDomainTabs(
  index: DomainIndex,
  documents: PidDocument[],
  parentDomainId: string,
): DomainTab[] {
  const childCounts = new Map<string, number>();
  let directCount = 0;
  let subtreeCount = 0;
  for (const doc of documents) {
    if (getDocumentTopDomainKey(index, doc) !== parentDomainId) continue;
    subtreeCount += 1;
    if (doc.domain_id === parentDomainId) {
      directCount += 1;
      continue;
    }
    if (doc.domain_id) childCounts.set(doc.domain_id, (childCounts.get(doc.domain_id) ?? 0) + 1);
  }
  if (childCounts.size === 0) return [];

  const childTabs: DomainTab[] = [];
  for (const [domainId, count] of childCounts) {
    childTabs.push({
      key: `domain:${domainId}`,
      label: domainNameById(index, domainId),
      count,
      scope: { kind: "domain", domainId },
    });
  }
  childTabs.sort(byLabel);

  const parentName = domainNameById(index, parentDomainId);
  const tabs: DomainTab[] = [
    {
      key: `subtree:${parentDomainId}`,
      label: `All ${parentName}`,
      count: subtreeCount,
      scope: { kind: "subtree", domainId: parentDomainId },
    },
    ...childTabs,
  ];
  if (directCount > 0) {
    tabs.push({
      key: `domain:${parentDomainId}`,
      label: "Direct",
      count: directCount,
      scope: { kind: "domain", domainId: parentDomainId },
    });
  }
  return tabs;
}

/** The top-row tab that owns a scope, so row one stays lit while row two moves. */
export function topKeyForScope(index: DomainIndex, scope: DomainScope): string {
  switch (scope.kind) {
    case "all":
      return "all";
    case "unassigned":
      return UNASSIGNED_DOMAIN;
    case "subtree":
      return scope.domainId;
    case "domain": {
      const node = index.get(scope.domainId);
      return node?.parentId ?? scope.domainId;
    }
  }
}

export { UNASSIGNED_DOMAIN, getDocumentDomainKey };
